/**
 * Putting a region's terrain surface into the scene — the one step that turns
 * `packages/engines flight/terrain-relief.ts`'s field into pixels.
 *
 * A sibling of `flight-scene.ts` rather than part of it, for the reason
 * `scene-draw.ts`, `scene-propellers.ts` and `scene-killcam.ts` are: that file
 * is at the size gate and new scene logic goes beside it.
 *
 * Two things happen, and the second is easy to forget. The relief mesh is added
 * to the region's own group, so it is disposed with everything else the region
 * owns. And the flat backdrop plane is **sunk below the relief's floor** — left
 * at the datum it would cut through every valley, since it is a 300 km quad and
 * the valleys are 50 m deep. Past the relief mesh's rim the backdrop is still
 * the only thing drawn, which is why it is lowered rather than removed.
 */

import type * as THREE from "three/webgpu";
import type { ReliefGround } from "../three/relief-ground.ts";
import { buildTerrainDecoration, type TerrainDecoration } from "../three/terrain-decoration.ts";
import { reliefIsFlat, type ReliefField } from "@red-opus/engine";
import type { TerrainFeatures } from "../world/terrain-features.ts";
import type { SkyDressing } from "./sky-dressing.ts";

/**
 * Extra metres the backdrop plane is dropped below the deepest point the relief
 * can reach. Enough that no valley floor ever meets it, small enough that the
 * step at the mesh rim — 80 km away, past the far plane — is never in frame.
 */
export const BACKDROP_CLEARANCE_M = 20;

export interface RegionRelief {
  readonly relief: ReliefField;
  readonly surface: ReliefGround | undefined;
  readonly features: TerrainFeatures;
  readonly instances: {
    readonly group: THREE.Group;
    /** ENHANCED TERRAIN's field hillshading, swapped with the toggle (`three/world-instancer.ts`). */
    readonly setLandcoverShading: (enabled: boolean) => void;
    /** The original's flat canopy discs, hidden while the enhanced foliage stands in for them. */
    readonly setCanopyDiscsVisible: (visible: boolean) => void;
    readonly shadedPlateCount: number;
  };
}

export interface TerrainSurface {
  /** Triangles the surface added, for the perf readout. 0 when the region is flat. */
  readonly triangleCount: number;
  /** Draw calls it added: 1, or 0 for a flat region. */
  readonly drawCallCount: number;
  /**
   * ENHANCED TERRAIN, both halves: the trees (built once, on the first request
   * that asks for them) and the **field hillshading**, which is the half a
   * pilot at altitude can actually see — the trees are 14 m and about 2 px at
   * 6 000 ft, whereas the shading repaints every field in frame.
   */
  readonly setDecoration: (enabled: boolean) => void;
  /** What the decoration costs, once it has been built; `undefined` until then. */
  readonly decoration: () => TerrainDecoration | undefined;
  /**
   * Walks the whole terrain down its own ramp for the hour.
   *
   * Live, because ENHANCED SKY is a toggle and the briefed sky word darkens the
   * ground under both treatments: before this, the sky and the
   * flat ground plane dimmed together and the *relief* stayed at noon, so a
   * night mission drew noon-bright fields under a black sky — and the
   * searchlight beams (AD-017) read as grey haze against them instead of light.
   * The ground half is a colour rewrite; the trees are rebuilt, which is why
   * the shift is remembered and re-applied if the decoration is switched on
   * later.
   */
  readonly setHourShift: (shift: number) => void;
}

/**
 * Builds and attaches the region's ground. A region with no relief
 * (`FLAT_RELIEF` — the two coastal maps, the practice range) draws nothing new
 * and keeps the flat backdrop it has always had, so this is a no-op there
 * rather than a special case downstream.
 */
export function applyRegionRelief(dressing: SkyDressing, region: RegionRelief): TerrainSurface {
  const { options } = region.relief;
  const reach = options.amplitudeM + options.riverDepthM;
  if (region.surface && !reliefIsFlat(region.relief)) {
    region.instances.group.add(region.surface.mesh);
    dressing.setBackdropFloor(-(reach + BACKDROP_CLEARANCE_M));
    return { ...decorationControl(region), triangleCount: region.surface.triangleCount, drawCallCount: 1 };
  }
  // A flat region (the two coastal maps, the practice range) keeps the ground
  // it has always had — but its decoration still works, because trees and water
  // do not need relief.
  return { ...decorationControl(region), triangleCount: 0, drawCallCount: 0 };
}

/**
 * Lazily builds the decoration the first time it is switched on, then keeps it
 * and only adds/removes the group. Rebuilding on every flip would pay
 * `dispose-tree.ts`'s pipeline-cache cost twice a click, which is the same
 * argument `sky-dressing.ts` makes for guarding its own re-dress.
 */
function decorationControl(
  region: RegionRelief,
): Pick<TerrainSurface, "setDecoration" | "decoration" | "setHourShift"> {
  let built: TerrainDecoration | undefined;
  let attached = false;
  let hourShift = 0;

  const build = (): TerrainDecoration =>
    buildTerrainDecoration(
      region.features.copseSites,
      // The same list the instancer painted the ground with — see
      // `world/terrain-features.ts` for why it is derived once.
      region.features.woodSites,
      region.surface?.drapeAt ?? (() => 0),
      hourShift,
    );

  return {
    decoration: () => built,
    setDecoration: (enabled: boolean) => {
      // Cheap and idempotent, so it is not guarded by `attached` — and it must
      // not be, because the shading is a buffer swap rather than a scene-graph
      // add and has no "already there" state of its own.
      region.instances.setLandcoverShading(enabled);
      // The heart of the enhancement: the original's canopies are flat filled
      // discs (AD-019 §2.2), and the decoration puts a solid tree on each of
      // the same trunks. Both at once is each tree drawn twice, so the discs go
      // away exactly while the foliage is up — and come straight back when it
      // is not, which is what keeps the authentic path pixel-identical.
      region.instances.setCanopyDiscsVisible(!enabled);
      if (enabled === attached) return;
      if (enabled) {
        built ??= build();
        region.instances.group.add(built.group);
      } else if (built) {
        region.instances.group.remove(built.group);
      }
      attached = enabled;
    },
    setHourShift: (shift: number) => {
      if (shift === hourShift) return;
      hourShift = shift;
      region.surface?.setRampShift(shift);
      // A tree's colours are baked into its four shared geometries, so the hour
      // means new ones. Only paid when the decoration is actually up, and only
      // on a toggle flip — a mission's hour does not change mid-flight.
      if (!built) return;
      const wasAttached = attached;
      if (wasAttached) region.instances.group.remove(built.group);
      built.dispose();
      built = build();
      if (wasAttached) region.instances.group.add(built.group);
    },
  };
}
